import { useCallback } from 'react';
import { cn } from '@/lib/utils';
import { CopyIcon, History } from 'lucide-react';
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from '@/components/ai-elements/conversation';
import {
  Message,
  MessageContent,
  MessageResponse,
  MessageActions,
  MessageAction,
} from '@/components/ai-elements/message';
import { Shimmer } from '@/components/ai-elements/shimmer';
import type { InterviewMessage, ExtractedEntity } from './useInterviewStore';

// ── Constants ──────────────────────────────────────────────────

const MARVIN = 'Marvin';

/**
 * All built-in types from the Core Ontology domain (v2).
 * Entities whose type is in this set get the solid-dot core indicator.
 */
const CORE_META_TYPES = new Set([
  'Entity', 'Process', 'Source', 'Rule', 'MasterData',
  'Actor', 'Spatial', 'Asset', 'Information', 'Temporal',
  'DataPoint', 'Quality', 'Situation',
  'Observation', 'ObservableProperty', 'Capability',
  'PhysicalObject', 'Plan', 'Agent', 'Role',
]);

type TypeStyle = { bg: string; text: string; border: string };

/** Color palette keyed by OntologyMetaType */
const TYPE_PALETTE: Record<string, TypeStyle> = {
  Observation:        { bg: 'bg-teal-500/15',    text: 'text-teal-400',    border: 'border-teal-500/30' },
  ObservableProperty: { bg: 'bg-teal-400/10',    text: 'text-teal-300',    border: 'border-teal-400/20' },
  Capability:         { bg: 'bg-cyan-500/15',    text: 'text-cyan-400',    border: 'border-cyan-500/30' },
  Agent:              { bg: 'bg-blue-600/15',    text: 'text-blue-400',    border: 'border-blue-600/30' },
  Actor:              { bg: 'bg-blue-500/15',    text: 'text-blue-400',    border: 'border-blue-500/30' },
  PhysicalObject:     { bg: 'bg-sky-500/15',     text: 'text-sky-400',     border: 'border-sky-500/30' },
  Role:               { bg: 'bg-blue-400/10',    text: 'text-blue-300',    border: 'border-blue-400/20' },
  Spatial:            { bg: 'bg-violet-500/15',  text: 'text-violet-400',  border: 'border-violet-500/30' },
  Asset:              { bg: 'bg-indigo-500/15',  text: 'text-indigo-400',  border: 'border-indigo-500/30' },
  Process:            { bg: 'bg-green-500/15',   text: 'text-green-400',   border: 'border-green-500/30' },
  Plan:               { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  Information:        { bg: 'bg-purple-500/15',  text: 'text-purple-400',  border: 'border-purple-500/30' },
  DataPoint:          { bg: 'bg-purple-400/10',  text: 'text-purple-300',  border: 'border-purple-400/20' },
  Quality:            { bg: 'bg-pink-500/15',    text: 'text-pink-400',    border: 'border-pink-500/30' },
  Situation:          { bg: 'bg-orange-500/15',  text: 'text-orange-400',  border: 'border-orange-500/30' },
  Rule:               { bg: 'bg-amber-500/15',   text: 'text-amber-400',   border: 'border-amber-500/30' },
  MasterData:         { bg: 'bg-yellow-500/15',  text: 'text-yellow-400',  border: 'border-yellow-500/30' },
  Entity:             { bg: 'bg-slate-500/15',   text: 'text-slate-300',   border: 'border-slate-500/30' },
  Source:             { bg: 'bg-slate-400/10',   text: 'text-slate-300',   border: 'border-slate-400/20' },
};

/** Fallback for domain-specific types not in the core set: dashed orange */
const DOMAIN_FALLBACK: TypeStyle = {
  bg: 'bg-orange-500/10',
  text: 'text-orange-300',
  border: 'border-orange-500/20 border-dashed',
};

function getTypeStyle(type: string): TypeStyle {
  return TYPE_PALETTE[type] ?? DOMAIN_FALLBACK;
}

// ── Helpers ─────────────────────────────────────────────────────

/**
 * Splits an assistant message into main body + trailing follow-up question.
 * The follow-up is the last paragraph (separated by blank lines) ending with "?".
 */
function splitFollowUp(content: string): { main: string; followUp: string | null } {
  const paragraphs = content.split(/\n{2,}/);
  if (paragraphs.length < 2) return { main: content, followUp: null };
  const last = paragraphs[paragraphs.length - 1].trim();
  if (last.endsWith('?')) {
    return { main: paragraphs.slice(0, -1).join('\n\n'), followUp: last };
  }
  return { main: content, followUp: null };
}

// ── Sub-components ───────────────────────────────────────────────

/** Small "Marvin" sender label shown above each assistant bubble */
function MarvinLabel() {
  return (
    <p className="mb-1 ml-0.5 text-[11px] font-semibold tracking-wide text-muted-foreground">
      {MARVIN}
    </p>
  );
}

/**
 * Colored entity pills rendered inline in the conversation after Marvin's
 * acknowledgment. Core-ontology types get a filled dot; domain-specific types
 * get a dimmed dot and a dashed border.
 */
function EntityBubbles({ entities }: { entities: ExtractedEntity[] }) {
  if (!entities.length) return null;
  return (
    <div className="mb-3 mt-2 ml-0.5">
      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
        Erkannte Entitäten
      </p>
      <div className="flex flex-wrap gap-1.5">
        {entities.map((e, i) => {
          const style = getTypeStyle(e.type);
          const isCore = CORE_META_TYPES.has(e.type);
          return (
            <span
              key={`${e.name}-${i}`}
              title={e.description ? `${e.type}: ${e.description}` : e.type}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium',
                style.bg, style.text, style.border,
              )}
            >
              {/* Dot: solid = core domain, faint = domain-specific */}
              <span
                className={cn(
                  'size-1.5 flex-shrink-0 rounded-full bg-current',
                  isCore ? 'opacity-80' : 'opacity-30',
                )}
              />
              {e.name}
              <span className="ml-0.5 text-[9px] opacity-40">{e.type}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

/** VS Code-style restore checkpoint button with dashed line */
function CheckpointMarker({
  messageId,
  isRestoring,
  onRestore,
}: {
  messageId: string;
  isRestoring?: boolean;
  onRestore: (id: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onRestore(messageId)}
      disabled={isRestoring}
      className="group my-2 flex w-full items-center gap-0 text-left disabled:pointer-events-none disabled:opacity-40"
    >
      <span
        className="inline-flex shrink-0 items-center gap-1 rounded bg-muted/60 px-1.5 py-px font-medium text-muted-foreground transition-colors group-hover:bg-muted group-hover:text-foreground"
        style={{ fontSize: '10px' }}
      >
        <History className="h-2.5 w-2.5" />
        Checkpoint wiederherstellen
      </span>
      <span className="ml-1 flex-1 border-t border-dashed border-border/50 transition-colors group-hover:border-border" />
    </button>
  );
}

// ── Main component ───────────────────────────────────────────────

interface ChatMessageListProps {
  messages: InterviewMessage[];
  streamingContent?: string;
  isStreaming?: boolean;
  isExtractingEntities?: boolean;
  isRestoring?: boolean;
  isCompleted?: boolean;
  onRestoreCheckpoint?: (messageId: string) => void;
}

/**
 * Renders the interview conversation with the new Marvin flow:
 *
 *   [Marvin]  greeting / acknowledgment text
 *   [User]    answer
 *   [Marvin]  "Marvin analysiert deine Antwort…" ← Shimmer while waiting
 *             → streams in response ←
 *             "Entitäten werden analysiert…"      ← Shimmer during extraction
 *   [Marvin]  main response (thanks + analysis)
 *             · Entity bubbles (colored, core vs. domain-specific)
 *             — Restore Checkpoint ──────────────────
 *   [Marvin]  follow-up question (split from response)
 *   [User]    answer …
 */
export function ChatMessageList({
  messages,
  streamingContent,
  isStreaming,
  isExtractingEntities,
  isRestoring,
  isCompleted,
  onRestoreCheckpoint,
}: ChatMessageListProps) {
  const handleCopy = useCallback((text: string) => {
    void navigator.clipboard.writeText(text);
  }, []);

  return (
    <Conversation className="flex-1">
      <ConversationContent className="px-4 py-6">
        {messages.map((msg, idx) => {
          // ── User bubble ──────────────────────────────────────
          if (msg.role === 'user') {
            return (
              <Message key={msg.id} from="user">
                <MessageContent>
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                </MessageContent>
              </Message>
            );
          }

          // ── Assistant / Marvin bubble ────────────────────────
          const isLast = idx === messages.length - 1;
          const isCurrentlyStreaming = Boolean(isStreaming) && isLast;

          // For completed messages: split off the trailing follow-up question
          const split = !isCurrentlyStreaming ? splitFollowUp(msg.content) : null;
          const mainContent = split?.main ?? msg.content;
          const followUp = split?.followUp ?? null;
          const entities = msg.metadata?.entities ?? [];

          // Checkpoint is shown after entities+followUp when user has already
          // replied (next message is a user message) and session is active
          const nextIsUser = messages[idx + 1]?.role === 'user';
          const showCheckpoint =
            !isCompleted &&
            !isStreaming &&
            !isRestoring &&
            !!onRestoreCheckpoint &&
            nextIsUser;

          return (
            <div key={msg.id} className="flex flex-col">

              {/* ── Phase 1: Shimmer while waiting for first streaming token ── */}
              {isCurrentlyStreaming && !streamingContent && (
                <>
                  <MarvinLabel />
                  <Message from="assistant">
                    <MessageContent>
                      <Shimmer duration={1.5}>Marvin analysiert deine Antwort…</Shimmer>
                    </MessageContent>
                  </Message>
                </>
              )}

              {/* ── Phase 2: Live streaming content ── */}
              {isCurrentlyStreaming && streamingContent && (
                <>
                  <MarvinLabel />
                  <Message from="assistant">
                    <MessageContent>
                      <MessageResponse>{streamingContent}</MessageResponse>
                    </MessageContent>
                  </Message>
                </>
              )}

              {/* ── Phase 3: Fully received assistant message ── */}
              {!isCurrentlyStreaming && (
                <>
                  {/* Entity bubbles — before Marvin's response because they
                      belong to the preceding user answer, not to Marvin's text */}
                  {entities.length > 0 && <EntityBubbles entities={entities} />}

                  <MarvinLabel />
                  <Message from="assistant">
                    <MessageContent>
                      <MessageResponse>{mainContent}</MessageResponse>
                    </MessageContent>
                    {/* Copy action on the last completed assistant message */}
                    {isLast && !isStreaming && (
                      <MessageActions>
                        <MessageAction
                          label="Kopieren"
                          onClick={() => handleCopy(msg.content)}
                        >
                          <CopyIcon className="size-3" />
                        </MessageAction>
                      </MessageActions>
                    )}
                  </Message>

                  {/* Restore Checkpoint — after Marvin's response, before follow-up */}
                  {showCheckpoint && (
                    <CheckpointMarker
                      messageId={msg.id}
                      isRestoring={isRestoring}
                      onRestore={onRestoreCheckpoint!}
                    />
                  )}

                  {/* Follow-up question as a separate Marvin bubble */}
                  {followUp && (
                    <>
                      <MarvinLabel />
                      <Message from="assistant">
                        <MessageContent>
                          <MessageResponse>{followUp}</MessageResponse>
                        </MessageContent>
                      </Message>
                    </>
                  )}
                </>
              )}
            </div>
          );
        })}

        {/* ── Text-mode thinking indicator ──────────────────────────────────
            In text mode the assistant placeholder is only added to `messages`
            AFTER the stream finishes.  While isStreaming is true the last
            message in the list is still the user message, so the per-message
            Shimmer above never fires.  We detect that situation here and
            render Marvin's thinking / live-streaming bubble below the list. */}
        {(() => {
          const lastMsg = messages[messages.length - 1];
          const waitingForAssistant = isStreaming && lastMsg?.role === 'user';
          if (!waitingForAssistant) return null;
          return (
            <>
              {/* Entity shimmer BEFORE Marvin's response — appears once extraction starts */}
              {isExtractingEntities && (
                <Shimmer duration={1.2} className="mb-2 text-xs">
                  Entitäten werden analysiert…
                </Shimmer>
              )}

              <MarvinLabel />
              <Message from="assistant">
                <MessageContent>
                  {streamingContent ? (
                    <MessageResponse>{streamingContent}</MessageResponse>
                  ) : (
                    <Shimmer duration={1.5}>Marvin analysiert deine Antwort…</Shimmer>
                  )}
                </MessageContent>
              </Message>
            </>
          );
        })()}
      </ConversationContent>
      <ConversationScrollButton />
    </Conversation>
  );
}
